<script setup>
import { ref, computed, watch, getCurrentInstance, onMounted } from 'vue'
import { isEmpty, cloneDeep } from 'lodash-es'
import store from '@/store'
import ReportControls from '@/components/ReportControls.vue'
import { compareChemicals } from '@/utils/chemical'
import { download, getDatedFilename, RESOLVER_URL, SAFETY_URL } from '@/main'
import { showErrorToast } from '@/utils/toast'
import { defaultVTooltipProps } from '@/utils/defaultProps'
import TableCardLayout from '@/components/report-page-layouts/TableCardLayout.vue'
import TableControls from '@/components/report-page-layouts/TableControls.vue'
import SaveDialog from '@/components/dialogs/SaveDialog.vue'
import ACLDialog from '@/components/auth/ACLDialog'
import ChemCell from '@/components/chemical/ChemCell.vue'
import PubchemInfoCard from '@/components/pubchem/PubchemInfoCard.vue'
import SafetyAppearanceSettings from '@/components/safety/SafetyAppearanceSettings.vue'
import ExportFilters from '@/components/safety/ExportFilters.vue'
import TransferMenu from '@/components/TransferMenu.vue'
import OptionsDropdown from '@/components/OptionsDropdown.vue'
import { getFlags } from '@/store/safety'
import { safetyAppearancePreference } from '@/components/safety/safetyPreferences'

const proxy = getCurrentInstance().proxy

const downloading = ref(false)
const profile = computed({
  get: () => safetyAppearancePreference.value.profile,
  set: (value) => {
    safetyAppearancePreference.value = {
      ...safetyAppearancePreference.value,
      profile: value ?? null,
    }
  },
})
const hideChemicalsWithNoSafety = computed({
  get: () => safetyAppearancePreference.value.hideND,
  set: (value) => {
    safetyAppearancePreference.value = {
      ...safetyAppearancePreference.value,
      hideND: Boolean(value),
    }
  },
})
const showImages = computed({
  get: () => safetyAppearancePreference.value.showImages,
  set: (value) => {
    safetyAppearancePreference.value = {
      ...safetyAppearancePreference.value,
      showImages: Boolean(value),
    }
  },
})
const titleFilter = ['Pictogram(s)', 'Signal', 'GHS Hazard Statements']
const search = ref(null)
const sections = ref([])
// const selectedStructures = ref([])
const imgWidth = ref(36)
const iconSize = ref(20)

const getStructures = computed(() => store.getters.getStructures)
const getCheckedStructures = computed(() => store.getters.getCheckedStructures)
const safetyResult = computed(() => store.getters.safetyResult)
const safetyProfiles = computed(() => store.getters.safetyProfiles)
const safetyEndpoints = computed(() => store.getters.safetyEndpoints)
const isAuthenticated = computed(() => store.getters.isAuthenticated)
const safetyLoading = computed(() => store.getters.getSafetyLoading)

const hasValidReportInputs = computed(() => Boolean(getCheckedStructures.value?.length))

const hasSafetyReport = computed(() => Boolean(safetyResult.value?.flags?.length))
const report = computed(() => (hasSafetyReport.value ? safetyResult.value : null))

const reportControlsStatus = computed(() => {
  if (safetyLoading.value) return 'loading'
  if (hasSafetyReport.value) return hasValidReportInputs.value ? 'stale' : 'fresh'
  return hasValidReportInputs.value ? 'ready' : 'invalid'
})

const appearanceFilters = computed(() => ({ hideND: hideChemicalsWithNoSafety.value }))

const profiles = computed(() => [
  { value: null, text: 'Full', sortable: false },
  ...safetyProfiles.value.map((item) => ({ value: item.id, text: item.name })),
])

const profileHeaders = computed(() => {
  const selectedProfile = safetyProfiles.value.find((item) => item.id === profile.value)
  const endpoints = profile.value == null || !selectedProfile ? safetyEndpoints.value : selectedProfile.endpoints
  return endpoints.map((item) => ({
    value: item.id,
    text: item.name,
    type: item.type,
    keyword: item.keyword,
    sortable: false,
    class: 'rotated-header',
  }))
})

const headers = computed(() => [
  {
    value: 'chemical',
    text: 'Chemical',
    sort: (left, right) => compareChemicals(left, right),
    class: 'position-sticky sticky-header bg-light',
    cellClass: 'position-sticky sticky-cell bg-light',
  },
  { value: 'sah', text: 'Safety', class: 'rotated-header' },
  { value: 'exp', text: 'Properties', class: 'rotated-header' },
  { value: 'flags.Signal', text: 'Signal', class: 'rotated-header' },
  ...profileHeaders.value,
  ...(profile.value == null
    ? [
        { value: 'rqCode.category', text: 'RQ Category', class: 'rotated-header' },
        { value: 'rqCode.rq', text: 'RQ in pounds (kilograms)', class: 'rotated-header' },
      ]
    : []),
])

const filteredFlags = computed(() =>
  report.value
    ? report.value.flags.filter(
        (flag) => !isEmpty(flag.flags) || flag.exp || flag.sah || !hideChemicalsWithNoSafety.value,
      )
    : [],
)

// const recordsForTransfer = computed(() => cloneDeep(selectedStructures.value))
const disableExport = computed(() => !report.value)
const getStructuresAsMap = computed(
  () => new Map(getStructures.value.map((structure) => [structure.chemical?.sid, structure])),
)

const exportItems = computed(() => [
  { title: 'Export to SDF', icon: 'mdi-file-download-outline', onClick: () => onExport('SDF') },
  { title: 'Export to Excel', icon: 'mdi-file-excel-outline', onClick: () => onExport('XLSX') },
  { title: 'Export to Word', icon: 'mdi-file-word-outline', onClick: () => onExport('DOCX') },
  { title: 'Export to PDF', icon: 'mdi-file-pdf-box', onClick: () => onExport('PDF') },
])
const onCalculateReport = () => {
  getFlags(getCheckedStructures.value, proxy)
}

const onClearReportControls = () => {
  sections.value = []
  search.value = null
  store.commit('resetSafetyResult')
}

const onAppearanceFiltersUpdate = (filters) => {
  hideChemicalsWithNoSafety.value = filters?.hideND ?? true
}

// const onReset = () => {
//   if (report.value?.id) {
//     proxy.$axios.delete(`${SAFETY_URL}/reports/${report.value.id}`).then(() => {
//       if (report.value.id === safetyResult.value?.id) store.commit('resetSafetyResult')
//       onClearReportControls()
//     })
//   } else {
//     onClearReportControls()
//   }
// }

const onAclUpdated = (acl) => {
  if (report.value) store.commit('setSafetyResult', { ...report.value, acl })
}

const onClickToReport = (chemical, section) => {
  if (section) proxy.$router.push(`/safety/reports/${chemical.sid}/${encodeURIComponent(section)}`)
  else proxy.$router.push(`/safety/reports/${chemical.sid}`)
}

const onExport = (format) => {
  downloading.value = true
  const selectedChemicalIds = new Set(selectedStructures.value.map((chemical) => chemical.chemId))

  const selectedFlags =
    selectedChemicalIds.size === 0
      ? report.value.flags
      : report.value.flags.filter((flag) => selectedChemicalIds.has(flag.chemical?.chemId))

  selectedFlags.forEach((flag) => {
    flag.chemical.additionalProps = getStructuresAsMap.value.get(flag.chemical.sid)?.chemical?.additionalProps
  })

  proxy.$axios
    .post(
      `${RESOLVER_URL}/export`,
      { flags: selectedFlags, outputFormat: format, showImages: showImages.value },
      { responseType: 'blob' },
    )
    .then((response) => {
      downloading.value = false
      download(getDatedFilename('safety', format.toLowerCase()), response.data, format.toLowerCase())
    })
    .catch((err) => {
      downloading.value = false
      showErrorToast(err, { title: 'Error during export' })
    })
}

const onReportSaved = (savedReport) => {
  savedReport.flags.forEach((item) => {
    item.chemical.checked = false
  })
  store.commit('setSafetyResult', cloneDeep(savedReport))
}

const onReportUpdated = (updatedReport) => {
  updatedReport.flags.forEach((item) => {
    item.chemical.checked = false
  })
  store.commit('setSafetyResult', cloneDeep(updatedReport))
}

const filterChemical = (value, s) => {
  if (!value) return false
  const q = s?.toLowerCase() || ''
  return (
    (value.sid && value.sid.toLowerCase().includes(q)) ||
    (value.name && value.name.toLowerCase().includes(q)) ||
    (value.casrn && value.casrn.toLowerCase().includes(q))
  )
}

const selectedRows = ref([])
const selectedStructures = computed(() => selectedRows.value.map((row) => row.chemical))

const filteredFlagsBySearch = computed(() =>
  filteredFlags.value.filter((row) => filterChemical(row.chemical, search.value)),
)

const onToggleSelectAll = ({ value }) => {
  const addAllFiltered = () => {
    const newRows = filteredFlagsBySearch.value.filter(
      (row) => !selectedRows.value.some((selectedRow) => selectedRow.chemical.chemId === row.chemical.chemId),
    )
    return [...selectedRows.value, ...newRows]
  }

  const substractSelected = () => {
    return selectedRows.value.filter(
      (row) => !filteredFlagsBySearch.value.some((item) => item.chemical.chemId === row.chemical.chemId),
    )
  }
  selectedRows.value = value ? addAllFiltered() : substractSelected()
}

watch(filteredFlags, (newValue) => {
  const filteredSelectedRows = newValue.filter((row) =>
    selectedRows.value.some((selectedRow) => selectedRow.chemical.chemId === row.chemical.chemId),
  )
  selectedRows.value = cloneDeep(filteredSelectedRows)
})

const maybeOpenChemCart = () => {
  if (!hasValidReportInputs.value && !hasSafetyReport.value) store.commit('SET_VIEW_STRUCTURES', true)
}

onMounted(() => {
  maybeOpenChemCart()
})
</script>

<script>
import AuthBase from '@/components/auth/AuthBase.vue'

export default {
  mixins: [AuthBase],
}
</script>

<template>
  <div class="d-flex flex-column h-100">
    <TableCardLayout>
      <template #table-controls>
        <TableControls :loading="safetyLoading">
          <template #left>
            <ReportControls
              :status="reportControlsStatus"
              calculate-id="safety-generate-button"
              clear-id="safety-claear-button"
              calculate-tooltip="Generate report for chemicals in ChemCart"
              calculate-aria-label="Generate report for chemicals in ChemCart"
              clear-aria-label="Clear Safety Report"
              :ui="{ group: 'mr-2', calculate: 'primary' }"
              @calculate="onCalculateReport"
              @clear="onClearReportControls"
            />
          </template>
          <template
            v-if="report"
            #right
          >
            <div class="d-flex align-center gap-1">
              <v-tooltip
                top
                dense
                v-bind="defaultVTooltipProps"
                :open-on-focus="false"
                :open-on-click="false"
              >
                <template #activator="{ on }">
                  <v-text-field
                    v-on="on"
                    v-model="search"
                    hide-details
                    :disabled="!report"
                    dense
                    class="grow"
                    style="max-width: 10rem"
                  >
                    <template #label>
                      <v-icon
                        small
                        left
                      >
                        mdi-magnify
                      </v-icon>
                      <span class="text-muted font-weight-light small-caption">Search chemical</span>
                    </template>

                    <template #append>
                      <v-icon
                        v-if="search"
                        small
                        class="cursor-pointer"
                        @click="search = ''"
                      >
                        mdi-close-circle
                      </v-icon>
                    </template>
                  </v-text-field>
                </template>

                Search by Name, CASRN or DTXSID
              </v-tooltip>

              <SafetyAppearanceSettings
                :filters="appearanceFilters"
                :show-images.sync="showImages"
                button-title="mdi-tune-variant"
                title="Display settings"
                max-width="420"
                :disabled="!report"
                @update:filters="onAppearanceFiltersUpdate"
              />

              <b-form-select
                id="safety-profile-select"
                v-model="profile"
                :disabled="!report"
                :options="profiles"
                class="safety-toolbar__profile"
                title="Safety assessment profile"
              />
              <v-tooltip
                top
                v-bind="defaultVTooltipProps"
                activator="#safety-profile-select"
              >
                Filter report by safety assessment profile (if applicable)
              </v-tooltip>

              <ExportFilters
                v-if="report"
                :sections.sync="sections"
                :hierarchy="report.sections"
                :report="report.flags"
                button-title="mdi-file-export-outline"
                title="Export arbitrary section(s) in one of the supported formats"
              />
            </div>

            <div class="d-flex align-center gap-1">
              <OptionsDropdown
                :loading="downloading"
                :items="exportItems"
                title="Export to SDF, Excel, Word or PDF"
                :disabled="disableExport"
                :label="`${selectedRows?.length}`"
                :show-label="!!selectedRows?.length"
              />

              <v-btn-toggle
                v-if="report && (isAuthenticated || canACL(report))"
                dense
              >
                <SaveDialog
                  v-if="isAuthenticated"
                  :api-url="`${SAFETY_URL}/reports`"
                  :button-title="report && report.id ? 'mdi-content-save-edit-outline' : 'mdi-content-save-outline'"
                  :item="report"
                  :title="report && report.id ? 'Edit Report' : 'Save Report'"
                  @itemCreated="onReportSaved"
                  @itemUpdated="onReportUpdated"
                />

                <ACLDialog
                  v-if="report && report.id && canACL(report)"
                  :api-url="SAFETY_URL"
                  :item="report"
                  button-title="mdi-account-key"
                  title="Access Control"
                  type="reports"
                  @aclUpdated="onAclUpdated"
                />
              </v-btn-toggle>

              <TransferMenu
                :records="selectedStructures"
                only-icon
                hide-add-button
              />
            </div>
          </template>
        </TableControls>
      </template>

      <v-data-table
        v-if="report"
        id="safety-table"
        v-model="selectedRows"
        :headers="headers"
        :items="filteredFlags"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 30, 50, -1],
        }"
        :items-per-page="10"
        :hide-default-footer="report.flags.length < 10"
        :search="search"
        :custom-filter="filterChemical"
        @toggle-select-all="onToggleSelectAll"
        class="w-100 h-100 d-flex flex-column"
        fixed-header
        show-select
        item-key="chemical.chemId"
      >
        <template #[`item.chemical`]="{ item }">
          <ChemCell
            :chemical="item.chemical"
            :show-images="showImages"
          />
        </template>

        <template #[`item.sah`]="{ item }">
          <a
            v-if="item.sah"
            href="javascript:void(0)"
            title="Safety and Hazards data available"
            v-b-tooltip
            @click="onClickToReport(item.chemical)"
          >
            <v-icon :size="iconSize">mdi-safety-goggles</v-icon>
          </a>
        </template>

        <template #[`item.exp`]="{ item }">
          <a
            v-if="item.exp"
            href="javascript:void(0)"
            title="Experimental Properties data available"
            v-b-tooltip
            @click="onClickToReport(item.chemical, 'Experimental Properties')"
          >
            <v-icon :size="iconSize">mdi-flask-empty-outline</v-icon>
          </a>
        </template>

        <template #[`item.flags.Signal`]="{ item }">
          <span
            :class="{
              'text-danger': item.flags.Signal === 'Danger',
              'text-info': item.flags.Signal === 'Warning',
            }"
          >
            {{ item.flags.Signal }}
          </span>
        </template>

        <template
          v-for="h in profileHeaders"
          #[`item.${h.value}`]="{ item, index }"
        >
          <span :key="h.value">
            <template v-if="h.type === 'ghs'">
              <template v-if="item.flags.GHS && item.flags.GHS.includes(h.keyword)">
                <v-menu
                  :close-on-content-click="false"
                  :open-delay="defaultVTooltipProps.openDelay"
                  open-on-hover
                  offset-x
                  role="none"
                >
                  <template #activator="{ on, attrs }">
                    <a
                      v-bind="attrs"
                      v-on="on"
                      href="javascript:void(0)"
                      :aria-label="h.keyword"
                      @click="onClickToReport(item.chemical, 'GHS Classification')"
                    >
                      <img
                        :id="`${h.value}-${index}`"
                        :src="`https://pubchem.ncbi.nlm.nih.gov/images/ghs/${h.keyword}.svg`"
                        :width="imgWidth"
                        :alt="h.keyword"
                      />
                    </a>
                  </template>

                  <PubchemInfoCard
                    :chemical="item.chemical"
                    section="GHS Classification"
                    :title-filter="titleFilter"
                  />
                </v-menu>
              </template>
            </template>

            <template v-else-if="h.type === 'nfpa'">
              <template v-if="item.flags.NFPA && item.flags.NFPA.length">
                <v-menu
                  :close-on-content-click="false"
                  :open-delay="defaultVTooltipProps.openDelay"
                  open-on-hover
                  offset-x
                  role="none"
                >
                  <template #activator="{ on, attrs }">
                    <a
                      href="javascript:void(0)"
                      v-bind="attrs"
                      v-on="on"
                      aria-label="NFPA Hazard Classification"
                      @click="onClickToReport(item.chemical, 'NFPA Hazard Classification')"
                    >
                      <img
                        :id="`${h.value}-${index}`"
                        v-for="(n, j) in item.flags.NFPA"
                        :key="j"
                        :src="`https://pubchem.ncbi.nlm.nih.gov/image/nfpa.cgi?code=${n}`"
                        :width="imgWidth"
                        alt="NFPA hazard symbol"
                      />
                    </a>
                  </template>

                  <PubchemInfoCard
                    section="NFPA Hazard Classification"
                    :chemical="item.chemical"
                  />
                </v-menu>
              </template>
            </template>

            <template v-else-if="item.flags[h.text]">
              <v-menu
                :close-on-content-click="false"
                :open-delay="defaultVTooltipProps.openDelay"
                open-on-hover
                offset-x
                role="none"
              >
                <template #activator="{ on, attrs }">
                  <a
                    href="javascript:void(0)"
                    :aria-label="h.text"
                    @click="onClickToReport(item.chemical, h.text)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon
                      :id="`${h.value}-${index}`"
                      :size="iconSize"
                    >
                      mdi-information-slab-circle-outline
                    </v-icon>
                  </a>
                </template>

                <PubchemInfoCard
                  :section="h.text"
                  :chemical="item.chemical"
                />
              </v-menu>
            </template>
          </span>
        </template>
      </v-data-table>
    </TableCardLayout>
  </div>
</template>

<style scoped lang="scss">
::v-deep #safety-table.v-data-table thead {
  line-height: 1;
  height: 7rem;
  text-align: center !important;
}

::v-deep #safety-table.v-data-table th {
  text-align: center !important;
}

::v-deep #safety-table.v-data-table th.rotated-header {
  writing-mode: sideways-lr;
  padding-left: 0.2rem;
  padding-right: 0.2rem;
  overflow: hidden;
  text-overflow: ellipsis;
}

::v-deep #safety-table .v-data-table__wrapper {
  display: flex;
  flex-direction: column;
  overflow: auto;
  flex-basis: 0 !important;
  flex-grow: 1 !important;
}

::v-deep #safety-table.v-data-table td {
  padding: 5px;
  text-align: center !important;
}
</style>
